import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, InputNumber, Select, message, Tag, Space, Card, Row, Col, Statistic, DatePicker, Popconfirm } from 'antd';
import { ReloadOutlined, CheckOutlined } from '@ant-design/icons';
import api from '../../utils/api';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;
const { Option } = Select;

const ThirdPartySettlements = () => {
  const [loading, setLoading] = useState(false);
  const [settlements, setSettlements] = useState([]);
  const [apps, setApps] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [stats, setStats] = useState({});
  const [filters, setFilters] = useState({});

  useEffect(() => {
    fetchApps();
    fetchStats();
  }, []);

  useEffect(() => {
    fetchSettlements();
  }, [page, filters]);

  const fetchApps = async () => {
    try {
      const response = await api.get('/admin/third-party/apps', { params: { limit: 100 } });
      setApps(response.data.list || []);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/admin/third-party/settlements/statistics');
      setStats(response.data || {});
    } catch (error) {
      console.error(error);
    }
  };

  const fetchSettlements = async () => {
    setLoading(true);
    try {
      const params = { page, limit, ...filters };
      if (filters.dateRange) {
        params.start_date = filters.dateRange[0].format('YYYY-MM-DD');
        params.end_date = filters.dateRange[1].format('YYYY-MM-DD');
        delete params.dateRange;
      }
      const response = await api.get('/admin/third-party/settlements', { params });
      setSettlements(response.data.list || []);
      setTotal(response.data.pagination?.total || 0);
    } catch (error) {
      message.error('获取结算列表失败');
    }
    setLoading(false);
  };

  const handleSettle = async (id) => {
    try {
      await api.post(`/admin/third-party/settlements/${id}/settle`);
      message.success('结算成功');
      fetchSettlements();
      fetchStats();
    } catch (error) {
      message.error(error.response?.data?.message || '结算失败');
    }
  };

  const handleBatchSettle = async () => {
    const selectedRows = settlements.filter(s => s.status === 'pending');
    if (selectedRows.length === 0) {
      message.warning('没有待结算的记录');
      return;
    }
    
    try {
      const ids = selectedRows.map(s => s.id);
      await api.post('/admin/third-party/settlements/batch-settle', { ids });
      message.success('批量结算成功');
      fetchSettlements();
      fetchStats();
    } catch (error) {
      message.error(error.response?.data?.message || '结算失败');
    }
  };

  const handleReset = () => {
    setFilters({});
    setPage(1);
  };

  const getStatusTag = (status) => {
    const statusMap = {
      pending: { color: 'orange', text: '待结算' },
      success: { color: 'green', text: '已结算' },
      failed: { color: 'red', text: '结算失败' }
    };
    const config = statusMap[status] || { color: 'default', text: status };
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  const columns = [
    {
      title: '结算ID',
      dataIndex: 'id',
      key: 'id',
      width: 80
    },
    {
      title: '订单号',
      dataIndex: 'trade_no',
      key: 'trade_no',
      width: 180
    },
    {
      title: '应用名称',
      dataIndex: 'app_name',
      key: 'app_name'
    },
    {
      title: '货币',
      dataIndex: 'currency_code',
      key: 'currency_code',
      render: (val) => val || 'CNY',
      width: 80
    },
    {
      title: '订单金额',
      dataIndex: 'order_amount',
      key: 'order_amount',
      render: (val, record) => {
        const currencyCode = record.currency_code || 'CNY';
        const symbols = { 'CNY': '¥', 'USD': '$', 'EUR': '€', 'GBP': '£', 'KES': 'KSh' };
        const symbol = symbols[currencyCode] || currencyCode;
        return `${symbol}${parseFloat(val).toFixed(2)}`;
      }
    },
    {
      title: '手续费率',
      dataIndex: 'fee_rate',
      key: 'fee_rate',
      render: (val) => `${(parseFloat(val) * 100).toFixed(2)}%`
    },
    {
      title: '手续费',
      dataIndex: 'fee_amount',
      key: 'fee_amount',
      render: (val, record) => {
        const currencyCode = record.currency_code || 'CNY';
        const symbols = { 'CNY': '¥', 'USD': '$', 'EUR': '€', 'GBP': '£', 'KES': 'KSh' };
        const symbol = symbols[currencyCode] || currencyCode;
        return `${symbol}${parseFloat(val).toFixed(2)}`;
      }
    },
    {
      title: '结算金额',
      dataIndex: 'settle_amount',
      key: 'settle_amount',
      render: (val, record) => {
        const currencyCode = record.currency_code || 'CNY';
        const symbols = { 'CNY': '¥', 'USD': '$', 'EUR': '€', 'GBP': '£', 'KES': 'KSh' };
        const symbol = symbols[currencyCode] || currencyCode;
        return `${symbol}${parseFloat(val).toFixed(2)}`;
      }
    },
    // {
    //   title: '汇率',
    //   dataIndex: 'exchange_rate',
    //   key: 'exchange_rate',
    //   render: (val) => val ? parseFloat(val).toFixed(4) : '-',
    //   width: 80
    // },
    // {
    //   title: '人民币金额',
    //   dataIndex: 'settle_amount_cny',
    //   key: 'settle_amount_cny',
    //   render: (val) => val ? `¥${parseFloat(val).toFixed(2)}` : '-'
    // },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: getStatusTag
    },
    {
      title: '结算时间',
      dataIndex: 'settle_time',
      key: 'settle_time',
      render: (time) => time ? dayjs(time).format('YYYY-MM-DD HH:mm:ss') : '-',
      width: 170
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (time) => time ? dayjs(time).format('YYYY-MM-DD HH:mm:ss') : '-',
      width: 170
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        record.status === 'pending' && (
          <Popconfirm title="确认结算?" onConfirm={() => handleSettle(record.id)}>
            <Button type="link" size="small" icon={<CheckOutlined />}>
              结算
            </Button>
          </Popconfirm>
        )
      )
    }
  ];

  return (
    <div>
      <Card>
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={6}>
            <Statistic title="结算总笔数" value={stats.total_count || 0} />
          </Col>
          <Col span={6}>
            <Statistic title="订单总金额" value={stats.total_order_amount_cny || 0} prefix="" precision={2} />
          </Col>
          <Col span={6}>
            <Statistic title="结算总金额" value={stats.total_settle_amount_cny || 0} prefix="" precision={2} />
          </Col>
          <Col span={6}>
            <Statistic title="待结算笔数" value={stats.pending_count || 0} />
          </Col>
        </Row>

        <div style={{ marginBottom: 16 }}>
          <Space wrap>
            <Select
              placeholder="选择应用"
              allowClear
              style={{ width: 150 }}
              onChange={(val) => setFilters({ ...filters, app_id: val })}
              value={filters.app_id}
            >
              {apps.map(app => (
                <Option key={app.app_id} value={app.app_id}>{app.app_name}</Option>
              ))}
            </Select>
            <Select
              placeholder="结算状态"
              allowClear
              style={{ width: 100 }}
              onChange={(val) => setFilters({ ...filters, status: val })}
              value={filters.status}
            >
              <Option value="pending">待结算</Option>
              <Option value="success">已结算</Option>
              <Option value="failed">失败</Option>
            </Select>
            <RangePicker 
              onChange={(dates) => setFilters({ ...filters, dateRange: dates })} 
              value={filters.dateRange}
            />
            <Button icon={<ReloadOutlined />} onClick={handleReset}>
              重置
            </Button>
            <Button type="primary" icon={<CheckOutlined />} onClick={handleBatchSettle}>
              批量结算
            </Button>
          </Space>
        </div>

        <Table
          columns={columns}
          dataSource={settlements}
          rowKey="id"
          loading={loading}
          pagination={{
            current: page,
            pageSize: limit,
            total: total,
            onChange: setPage,
            showSizeChanger: false
          }}
        />
      </Card>
    </div>
  );
};

export default ThirdPartySettlements;
